/**
 * LiabilityForm — the liability-side form matching the legacy
 * Liabilities.js layout.
 *
 * Writes to `liabilities` with the following schema (unchanged):
 *   { name, kind, outstanding, principal_amount?, emi_amount?,
 *     interest_rate?, currency, notes? }
 *
 * Kind pill selector mirrors Liabilities.js. Credit cards are NOT
 * offered here — they live in `accounts` with type='credit_card'; the
 * router in AccountFormDialog dispatches those to CashAccountForm.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CurrencyInput } from '@/components/ui/CurrencyInput';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectSeparator } from '@/components/ui/select';
import { DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Home, Car, User as UserIcon, GraduationCap, FileText, Zap } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { CURRENCIES } from '@/lib/format';
import useCurrencyOptions from './useCurrencyOptions';

const KIND_PILLS = [
  { value: 'home_loan',      label: 'Home loan',       icon: Home,          placeholder: 'Home loan / mortgage @ 8.4%' },
  { value: 'car_loan',       label: 'Car loan',        icon: Car,           placeholder: 'Car loan @ 9.1%' },
  { value: 'personal_loan',  label: 'Personal loan',   icon: UserIcon,      placeholder: 'Personal loan @ 13%' },
  { value: 'education_loan', label: 'Education loan',  icon: GraduationCap, placeholder: 'Student loan @ 10%' },
  { value: 'bnpl',           label: 'BNPL',            icon: Zap,           placeholder: 'Tabby, Tamara, Postpay…' },
  { value: 'other_debt',     label: 'Other',           icon: FileText,      placeholder: 'Other debt…' },
];

// v3.7.5 Phase 84.29 — heuristic mapping from raw liability name to kind.
// When a user types the name of a well-known BNPL provider we default
// the `kind` pill to 'bnpl' so downstream analytics + interest-rate
// rules treat it consistently. Users can still override.
const BNPL_NAME_HINTS = /\b(tabby|tamara|postpay|klarna|afterpay|affirm|zip pay|zippay|clearpay|sezzle|spotii|cashew|split)\b/i;
function detectKindFromName(name) {
  if (!name) return null;
  if (BNPL_NAME_HINTS.test(name)) return 'bnpl';
  return null;
}

function currencySymbol(code) {
  return (CURRENCIES.find((c) => c.code === code) || {}).symbol || '';
}

export default function LiabilityForm({ account = null, onCreate, onUpdate, onClose, baseCurrency = 'INR' }) {
  const { user } = useAuth();
  const editing = !!account;
  const currencyOpts = useCurrencyOptions();

  const [form, setForm] = useState({
    kind: 'home_loan',
    name: '',
    outstanding: '',
    currency: baseCurrency,
    principal_amount: '',
    emi_amount: '',
    interest_rate: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing && account?.raw) {
      const r = account.raw;
      setForm({
        kind: r.kind || 'home_loan',
        name: r.name || '',
        outstanding:      r.outstanding      != null ? String(r.outstanding)      : '',
        currency:         r.currency         || baseCurrency,
        principal_amount: r.principal_amount != null ? String(r.principal_amount) : '',
        emi_amount:       r.emi_amount       != null ? String(r.emi_amount)       : '',
        interest_rate:    r.interest_rate    != null ? String(r.interest_rate)    : '',
        notes: r.notes || '',
      });
    }
  }, [editing, account, baseCurrency]);

  const save = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    if (!form.name.trim()) { toast.error('Please give this a name.'); return; }
    if (!form.currency)    { toast.error('Please choose a currency.'); return; }
    if (!form.outstanding || Number(form.outstanding) <= 0) {
      toast.error('Please enter the outstanding balance.'); return;
    }
    setSaving(true);
    try {
      const payload = {
        kind: form.kind,
        name: form.name.trim(),
        outstanding: Number(form.outstanding),
        currency: form.currency,
        principal_amount: form.principal_amount ? Number(form.principal_amount) : null,
        emi_amount:       form.emi_amount       ? Number(form.emi_amount)       : null,
        interest_rate:    form.interest_rate    ? Number(form.interest_rate)    : null,
        notes: form.notes?.trim() || null,
      };
      if (editing) await onUpdate('liabilities', account.id, payload);
      else         await onCreate('liabilities', payload, user?.id);
      toast.success(editing ? 'Saved.' : 'Added.');
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Could not save.');
    } finally { setSaving(false); }
  };

  const symbol = currencySymbol(form.currency || baseCurrency);
  const activePill = KIND_PILLS.find((k) => k.value === form.kind) || KIND_PILLS[0];

  return (
    <form onSubmit={save} className="space-y-4" data-testid="accounts-form-liability">
      {/* Kind pill selector */}
      <div className="space-y-2">
        <Label>Type</Label>
        <div className="grid grid-cols-3 gap-2">
          {KIND_PILLS.map((k) => {
            const Icon = k.icon;
            const active = form.kind === k.value;
            return (
              <button
                key={k.value}
                type="button"
                onClick={() => setForm({ ...form, kind: k.value })}
                className={
                  'flex flex-col items-start gap-1.5 rounded-xl border px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ' +
                  (active
                    ? 'border-evergreen bg-sage-light/40 text-evergreen'
                    : 'border-border/70 hover:bg-muted/50 text-charcoal')
                }
                data-testid={`liab-kind-pill-${k.value}`}
              >
                <Icon className={'h-4 w-4 ' + (active ? 'text-evergreen' : 'text-muted-foreground')} />
                <span className="text-sm font-medium">{k.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Name */}
      <div className="space-y-1.5">
        <Label>Name</Label>
        <Input
          value={form.name}
          onChange={(e) => {
            const nextName = e.target.value;
            // v3.7.5 Phase 84.29 — auto-nudge kind → 'bnpl' when the
            // user types a recognised BNPL provider name. We only
            // override when the pill hasn't been manually touched
            // (still on the default `home_loan`) so we never fight
            // an explicit choice.
            const suggested = detectKindFromName(nextName);
            if (suggested && form.kind === 'home_loan') {
              setForm({ ...form, name: nextName, kind: suggested });
            } else {
              setForm({ ...form, name: nextName });
            }
          }}
          placeholder={activePill.placeholder}
          className="rounded-xl"
          data-testid="liab-name-input"
          autoFocus
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Outstanding</Label>
          <CurrencyInput
            symbol={symbol}
            currency={form.currency}
            value={form.outstanding}
            onChange={(e) => setForm({ ...form, outstanding: e.target.value })}
            placeholder="0"
            data-testid="liab-outstanding-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Currency</Label>
          <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
            <SelectTrigger className="rounded-xl" data-testid="liab-currency-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-64">
              {currencyOpts.top.map((c) => (
                <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code} — {c.label}</SelectItem>
              ))}
              <SelectSeparator />
              {currencyOpts.rest.map((c) => (
                <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code} — {c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Original principal (optional)</Label>
          <CurrencyInput
            symbol={symbol}
            currency={form.currency}
            value={form.principal_amount}
            onChange={(e) => setForm({ ...form, principal_amount: e.target.value })}
            placeholder="0"
            data-testid="liab-principal-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Interest rate % (optional)</Label>
          <Input
            type="number" inputMode="decimal" step="0.01"
            value={form.interest_rate}
            onChange={(e) => setForm({ ...form, interest_rate: e.target.value })}
            className="rounded-xl tabular-nums"
            placeholder="e.g. 8.4"
            data-testid="liab-interest-input"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Monthly payment / EMI (optional)</Label>
        <CurrencyInput
          symbol={symbol}
          currency={form.currency}
          value={form.emi_amount}
          onChange={(e) => setForm({ ...form, emi_amount: e.target.value })}
          placeholder="0"
          data-testid="liab-emi-input"
        />
      </div>

      <div className="space-y-1.5">
        <Label>Notes (optional)</Label>
        <Input
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className="rounded-xl"
          data-testid="liab-notes-input"
        />
      </div>

      <DialogFooter className="sm:justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onClose} disabled={saving} className="rounded-xl">Cancel</Button>
        <Button type="submit" disabled={saving} className="rounded-xl" data-testid="liab-save-button">
          {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
          {editing ? 'Save changes' : 'Save'}
        </Button>
      </DialogFooter>
    </form>
  );
}
